import { IContextualMenuItem, IIconProps } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { FileTypeEnum } from "../../../src/taskpane/components/enums/fileTypeEnum";
import { FolderTypeIconEnum } from "../../../src/taskpane/components/enums/FolderTypeIconEnum";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import { ISearchOpenFileInfo } from "../../../src/taskpane/interfaces/ISearchOpenFileInfo";
import { IStoreItem } from "../../../src/taskpane/interfaces/IStoredItem";
import { RootState } from "../../../src/taskpane/redux";
import { addPinnedItem, isFilePinned, isFolderPinned, removePinnedItem } from "../../../src/taskpane/redux/modules/pinnedItems";
import { getFileItemIdFromRelativePath, getItemWebViewUrl, getListItemWebUrl } from "../../../src/taskpane/services/GraphService";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";

const unpinIconProps: IIconProps = { iconName: "Unpin" };
const pinIconProps: IIconProps = { iconName: "Pin" };

export function useMenuItem_Pin(selectedItems: any[], waitFor: boolean, isInOneDrive: boolean, selectedItemInfo?: ISearchOpenFileInfo) {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();
    
    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const pinnedItems = useSelector((state: RootState) => state.pinned?.pinnedItems);

    const [isSelectedItemPinned, setIsSelectedItemPinned] = React.useState<boolean>(false);
    const [menuItem_Pin, setMenuItem_Pin] = React.useState<IContextualMenuItem>();
    
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    
    React.useEffect(() => {
      (async () => {
        if (selectedItems?.length === 1 && currentDirectory.type !== ComponentTypes.SharePointList) {
          let isItemPinned: boolean;
  
          const selectedGridItem = selectedItems[0];
          let driveId: string = !isInOneDrive ? (!currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId) : oneDriveRootDetails.driveId;
          let itemId: string;
          const spItemUrlparts = selectedGridItem[".spItemUrl"]?.split('/');
          if (spItemUrlparts?.length > 2) {
            if (!driveId) {
              driveId = spItemUrlparts[spItemUrlparts.length - 2]?.trim();
            }
            itemId = spItemUrlparts[spItemUrlparts.length - 1]?.split('?')[0];
          }
  
          if (!itemId) {
            const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
            const itemUrl = new URL(driveUrl.origin + selectedGridItem.FileRef);
            itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, itemUrl.pathname.replace(driveUrl.pathname, ""), false);
          }
  
          switch (selectedGridItem.FSObjType) {
            case "0": // File
              isItemPinned = isFilePinned(itemId, pinnedItems);
              break;
  
            case "1": // Folder
              isItemPinned = isFolderPinned(driveId, itemId, pinnedItems);
              break;
          }
  
          setIsSelectedItemPinned(isItemPinned);
        }
      })();
    }, [pinnedItems, selectedItems, currentDirectory]);
    
    React.useEffect(() => {
      if (selectedItems.length === 1 && currentDirectory.type !== ComponentTypes.SharePointList) {
        setMenuItem_Pin(
          {
            key: "pin",
            disabled: waitFor === true,
            text: selectedItems[0]?.FSObjType === "0"
              ? (isSelectedItemPinned ? languageStrings.UnpinFile : languageStrings.PinThisFile)
              : (isSelectedItemPinned ? languageStrings.UnpinFolder : languageStrings.PinThisFolder),
            iconProps: isSelectedItemPinned ? unpinIconProps : pinIconProps,
            onClick: pinUnpinItem,
          }
        )
      }
      else {
        setMenuItem_Pin(null);
      }
    }, [selectedItems, isSelectedItemPinned, waitFor, currentDirectory]);

    const pinUnpinItem = React.useCallback((ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, item?: IContextualMenuItem) => {
      (async () => {
        try {
          let driveId: string = !isInOneDrive ? (!currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId) : oneDriveRootDetails.driveId;
          let itemId: string;
          const spItemUrlparts = selectedItems[0][".spItemUrl"]?.split('/');
          if (spItemUrlparts?.length > 2) {
            if (!driveId) {
              driveId = spItemUrlparts[spItemUrlparts.length - 2]?.trim();
            }
            itemId = spItemUrlparts[spItemUrlparts.length - 1]?.split('?')[0];
          }
    
          const accountId: string = (await authModule.getAccount())?.accountId;
    
          if (isSelectedItemPinned) {
            dispatch(removePinnedItem(accountId, { driveId: driveId, itemId: itemId }, selectedItems[0].FSObjType === "0" ? FileTypeEnum.file : FileTypeEnum.folder));
            setIsSelectedItemPinned(false);
          }
          else {
            let itemToPin: IStoreItem;
            switch (selectedItems[0].FSObjType) {
              case "0": // File
                itemToPin = {
                  driveId: driveId,
                  name: selectedItems[0].FileLeafRef,
                  extension: selectedItems[0].File_x0020_Type,
                  itemId: itemId,
                  folderId: !currentDirectory?.parentId ? null : currentDirectory.id,
                  fileType: FileTypeEnum.file,
                  filePath: `[${languageStrings.Files}] ${workingDirectories.map(x => x.displayName).join("/")}/${selectedItems[0].FileLeafRef}`,
                  lastModifiedDateTime: selectedItems[0].Modified,
                  datePinned: new Date(),
                  modifiedBy: Array.isArray((selectedItems[0].Editor ?? selectedItems[0].Author)) && (selectedItems[0].Editor ?? selectedItems[0].Author).length > 0 && (selectedItems[0].Editor ?? selectedItems[0].Author)[0]?.title
                };
                break;
    
              case "1": // Folder
                let locations: IPageSettings[] = [...workingDirectories];
                if (isInOneDrive) {
                  locations.push({
                    id: itemId,
                    parentId: driveId,
                    fileTitle: selectedItems[0].FileLeafRef,
                    displayName: selectedItems[0].FileLeafRef,
                    webUrl: currentDirectory.webUrl,
                    type: ComponentTypes.OneDriveSubFolder,
                    directory: !currentDirectory.directory
                      ? `${selectedItems[0].FileLeafRef}`
                      : `${currentDirectory.directory}/${selectedItems[0].FileLeafRef}`,
                    DocumentLibrary: "",
                    sharePointListId: currentDirectory?.sharePointListId,
                  });
                }
                else {
                  locations.push({
                    id: itemId,
                    parentId: driveId,
                    fileTitle: selectedItems[0].FileLeafRef,
                    displayName: selectedItems[0].FileLeafRef,
                    webUrl: currentDirectory.webUrl,
                    siteId: currentDirectory.siteId,
                    siteDisplayName: currentDirectory.siteDisplayName,
                    type: ComponentTypes.SharePointFolders,
                    directory: !currentDirectory.directory
                      ? `${selectedItems[0].FileLeafRef}`
                      : `${currentDirectory.directory}/${selectedItems[0].FileLeafRef}`,
                    DocumentLibrary: currentDirectory.DocumentLibrary?.length > 0 ? currentDirectory.DocumentLibrary : selectedItems[0].FileLeafRef,
                    isTeamsFolder: currentDirectory.isTeamsFolder,
                    isTeamsRootFolder: false,
                    sharePointListId: currentDirectory?.sharePointListId,
                    contentTypeId: selectedItems[0].ContentTypeId
                  });
                }
    
                itemToPin = {
                  driveId: driveId,
                  locations: [...locations],
                  name: selectedItems[0].FileLeafRef,
                  itemId: itemId,
                  fileType: FileTypeEnum.folder,
                  folderTypeIcon: isInOneDrive ? FolderTypeIconEnum.oneDriveSubFolder : FolderTypeIconEnum.sharepointFolder,
                  datePinned: new Date(),
                  folderId: itemId,
                  sharePointListId: currentDirectory?.sharePointListId,
                  contentTypeId: selectedItems[0].ContentTypeId
                };
                break;
            }
    
            dispatch(addPinnedItem(accountId, itemToPin));
            setIsSelectedItemPinned(true);
          } 
        } catch (error) {
          logger(error, "useMenuItem_Pin.tsx (pinUnpinItem)");
        }
      })();
    }, [currentDirectory, selectedItems, isSelectedItemPinned]);
   
    return {
        menuItem_Pin
    }

}